import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { get } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen } from "../../../components/admin/EssUi";
import { BarChart3, Search } from "lucide-react";

const RATING_RANGES = [
  { range: "90%+", min: 90, color: "#16a34a" },
  { range: "80-89%", min: 80, color: "#4f46e5" },
  { range: "70-79%", min: 70, color: "#d97706" },
  { range: "60-69%", min: 60, color: "#db2777" },
  { range: "<60%", min: 0, color: "#e11d48" },
];

const DEPT_COLORS = ["#4f46e5","#16a34a","#d97706","#7c3aed","#0891b2","#db2777","#9333ea","#ea580c"];

const ROWS_PER_PAGE = 10;

const AdminPanelAppraisalsPage = () => {
  const intl = useIntl();
  const { permissions } = useSelector((state) => state.auth) || {};
  const canManage = !!permissions?.canManageAppraisals;
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const [apprRes, empRes, deptRes] = await Promise.all([
          get("/api/appraisals"),
          get("/api/employees?limit=500"),
          get("/api/departments"),
        ]);
        const apprRows = apprRes?.data || [];
        const empRows = empRes?.data || [];
        const deptRows = deptRes?.data || [];
        const empMap = {};
        empRows.forEach((e) => { empMap[e.id] = e; });
        const deptMap = {};
        deptRows.forEach((d) => { deptMap[d.id] = d.complete_name || d.name; });

        setData(apprRows.map((e, i) => ({
          id: e.id || i,
          employee: (empMap[e.employee_id] || {}).name || "—",
          department: e.department_id ? (deptMap[e.department_id] || e.department_id) : "—",
          reviewType: "Performance Review",
          period: e.date ? new Date(e.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : "—",
          manager: (empMap[e.manager_id] || {}).name || "—",
          selfReview: e.state === "draft" || e.state === "DRAFT" ? "Pending" : "Submitted",
          mgrReview: e.state === "confirm" || e.state === "PENDING" ? "In Progress" : e.state === "done" || e.state === "APPROVED" ? "Submitted" : "—",
          score: e.final_score ? `${e.final_score}%` : "—",
          status: e.state === "draft" || e.state === "DRAFT" ? "Pending" : e.state === "confirm" || e.state === "PENDING" ? "In Progress" : e.state === "done" || e.state === "APPROVED" ? "Completed" : e.state || "—",
        })));
      } catch { setData([]); }
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return <LoadingScreen />;

  const depts = [...new Set(data.map(r => r.department))].filter(d => d !== "—");
  const types = [...new Set(data.map(r => r.reviewType))];

  const getTabFilter = () => {
    if (activeTab === "inprogress") return { key: "status", value: "In Progress" };
    if (activeTab === "completed") return { key: "status", value: "Completed" };
    if (activeTab === "pending") return { key: "status", value: "Pending" };
    return null;
  };
  const tabFilter = getTabFilter();

  const filtered = data.filter(r => {
    if (tabFilter && r.status !== tabFilter.value) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (deptFilter !== "all" && r.department !== deptFilter) return false;
    if (typeFilter !== "all" && r.reviewType !== typeFilter) return false;
    if (search.trim() && !r.employee.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const paginatedData = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const tabs = [
    { key: "all", label: "All Reviews", count: data.length },
    { key: "inprogress", label: "In Progress", count: data.filter(r => r.status === "In Progress").length },
    { key: "completed", label: "Completed", count: data.filter(r => r.status === "Completed").length },
    { key: "pending", label: "Pending", count: data.filter(r => r.status === "Pending").length },
  ];

  const getStatusColor = (status) => {
    const colors = { "Pending": "#d97706", "In Progress": "#4f46e5", "Completed": "#16a34a" };
    return colors[status] || "#64748b";
  };

  const getStatusBg = (status) => {
    const colors = { "Pending": "#fef3c7", "In Progress": "#eef2ff", "Completed": "#dcfce7" };
    return colors[status] || "#f1f5f9";
  };

  const getScoreColor = (score) => {
    if (!score || score === "—") return "#94a3b8";
    const num = parseInt(score);
    if (num >= 90) return "#16a34a";
    if (num >= 75) return "#4f46e5";
    if (num >= 60) return "#d97706";
    return "#e11d48";
  };

  const withScores = data.filter(r => r.score !== "—");
  const scoreNums = withScores.map(r => parseInt(r.score)).filter(n => !isNaN(n));
  const avgScore = scoreNums.length ? Math.round((scoreNums.reduce((s, n) => s + n, 0) / scoreNums.length) * 10) / 10 : null;

  const ratingDistribution = RATING_RANGES.map((rng, idx) => {
    const count = scoreNums.filter(n => {
      if (idx === 0) return n >= 90;
      if (idx === RATING_RANGES.length - 1) return n < 60;
      return n >= rng.min && n < (RATING_RANGES[idx - 1].min);
    }).length;
    return { range: rng.range, count, color: rng.color };
  });
  const excellent = scoreNums.filter(n => n >= 90).length;
  const good = scoreNums.filter(n => n >= 75 && n < 90).length;
  const needsImprovement = scoreNums.filter(n => n < 75).length;

  const completedByDept = {};
  const totalByDept = {};
  data.forEach(r => {
    if (r.department !== "—") {
      totalByDept[r.department] = (totalByDept[r.department] || 0) + 1;
      if (r.status === "Completed") completedByDept[r.department] = (completedByDept[r.department] || 0) + 1;
    }
  });
  const completionByDept = Object.keys(totalByDept).map((dept, i) => ({
    dept,
    rate: Math.round((completedByDept[dept] || 0) / totalByDept[dept] * 100),
    color: DEPT_COLORS[i % DEPT_COLORS.length],
  }));
  const maxRate = Math.max(...completionByDept.map(d => d.rate), 1);
  const performanceSummary = [
    { label: "Avg Score", value: avgScore !== null ? `${avgScore}%` : "—" },
    { label: "Excellent (90%+)", value: `${excellent} employees`, color: "#16a34a" },
    { label: "Good (75–89%)", value: `${good} employees`, color: "#4f46e5" },
    { label: "Needs Improvement", value: `${needsImprovement} employees`, color: "#d97706" },
    { label: "Promotions Triggered", value: `${data.filter(r => r.status === "Completed" && avgScore !== null && parseInt(r.score) >= 90).length}`, color: "#7c3aed" },
    { label: "PIPs Initiated", value: `${data.filter(r => r.status === "Completed" && parseInt(r.score) !== null && parseInt(r.score) < 75).length}`, color: "#e11d48" },
  ];

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader
        title="Appraisals"
        subtitle="Manage performance reviews and employee ratings"
        actions={
          <div style={{ display: 'flex', gap: '12px' }}>
            {canManage && (
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm" style={{ background: "#059669", color: "white", border: 'none', cursor: 'pointer' }}>
              ＋ New Review Cycle
            </button>
            )}
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all" style={{ background: "#f1f5f9", color: "#334155", border: 'none', cursor: 'pointer' }}>
              <BarChart3 size={16} />
              Reports
            </button>
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: '24px' }}>
        <StatCard label="In Progress" value={data.filter(r => r.status === "In Progress").length} icon="🔄" color="blue" trend="Current cycle" />
        <StatCard label="Pending Self-Review" value={data.filter(r => r.status === "Pending").length} icon="📝" color="amber" trend="Awaiting submission" />
        <StatCard label="Manager Reviews Due" value={data.filter(r => r.mgrReview === "In Progress").length} icon="⏰" color="rose" trend="Awaiting manager" />
        <StatCard label="Completed" value={data.filter(r => r.status === "Completed").length} icon="✅" color="green" trend="All time" />
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPage(1); }}
            style={{
              padding: '8px 20px', border: 'none', background: activeTab === tab.key ? '#059669' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#475569', borderRadius: '8px', fontSize: '13px',
              fontWeight: activeTab === tab.key ? '600' : '500', cursor: 'pointer', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#f1f5f9', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px', background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search employee..."
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', background: '#f8fafc' }}
            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
        <select value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Status</option>
          <option value="Pending">Pending</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
        </select>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">Review Types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gap: '24px' }} className="ess-rg-2-1">
        <div>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Employee</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Department</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Review Type</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manager</th>
                    <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Self-Review</th>
                    <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mgr Review</th>
                    <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Score</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No appraisals found</td></tr>
                  ) : paginatedData.map((row, index) => (
                    <tr key={row.id} style={{ borderBottom: index < paginatedData.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{row.employee}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b' }}>{row.department}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: '#f1f5f9', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', color: '#475569' }}>{row.reviewType}</span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b' }}>{row.period}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569' }}>{row.manager}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ background: row.selfReview === "Submitted" ? '#dcfce7' : '#fef3c7', color: row.selfReview === "Submitted" ? '#16a34a' : '#d97706', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>{row.selfReview}</span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ background: row.mgrReview === "In Progress" ? '#eef2ff' : row.mgrReview === "Submitted" ? '#dcfce7' : '#f1f5f9', color: row.mgrReview === "In Progress" ? '#4f46e5' : row.mgrReview === "Submitted" ? '#16a34a' : '#94a3b8', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>{row.mgrReview || "—"}</span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        {row.score !== "—" ? (
                          <span style={{ fontWeight: '700', color: getScoreColor(row.score), fontSize: '14px' }}>{row.score}</span>
                        ) : <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: getStatusBg(row.status), color: getStatusColor(row.status), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{row.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === 1 ? '#f8fafc' : 'white', color: page === 1 ? '#94a3b8' : '#059669', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
              >← Previous</button>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === totalPages ? '#f8fafc' : 'white', color: page === totalPages ? '#94a3b8' : '#059669', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
              >Next →</button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
             <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Rating Distribution</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {ratingDistribution.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No rating data available</div>
              ) : ratingDistribution.map((item) => (
                <div key={item.range}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '3px' }}>
                    <span style={{ color: '#64748b' }}>{item.range}</span>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>{item.count}</span>
                  </div>
                  <div style={{ height: 8, background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${item.count === 0 ? 0 : (item.count / Math.max(...ratingDistribution.map(d => d.count), 1)) * 100}%`, background: item.color, borderRadius: '4px', transition: 'width 1s ease' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Completion Rate by Dept</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {completionByDept.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No department data available</div>
              ) : completionByDept.map((item) => (
                <div key={item.dept}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '3px' }}>
                    <span style={{ color: '#64748b' }}>{item.dept}</span>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>{item.rate}%</span>
                  </div>
                  <div style={{ height: 6, background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(item.rate / maxRate) * 100}%`, background: item.color, borderRadius: '3px', transition: 'width 1s ease' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Performance Summary</h3>
            {performanceSummary.map((item) => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>{item.label}</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: item.color || '#1e293b' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanelAppraisalsPage;
